import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/dist/query/react";

import { API_ENDPOINT } from "../../config";
import { ManifestCreateResponse } from "../../interfaces";
import { fulfilmentApi } from "../fulfilment/fulfilment.api";

export type CreateManifestPayload = {
  sku_item_ids: string[];
  sku_code?: string;
};

export type BulkOrderResultRow = {
  cart_order_id: string;
  message: string;
  students?: Array<{ name: string; registration_number: string | null }>;
};

export type BulkManifestResponse = {
  succeeded: Array<{
    cart_order_id: string;
    manifest_id?: string;
    item_count: number;
    sku_code_filter?: string;
  }>;
  skipped: BulkOrderResultRow[];
  failed: BulkOrderResultRow[];
  success_count: number;
  skipped_count: number;
  failed_count: number;
};

export type BulkManifestPdfResult = {
  blob: Blob;
  success_count: number;
  skipped_count: number;
  failed_count: number;
  skipped: BulkOrderResultRow[];
  failed: BulkOrderResultRow[];
};

function parseBulkResultsHeader(header: string | null): {
  skipped: BulkOrderResultRow[];
  failed: BulkOrderResultRow[];
  success_count?: number;
  skipped_count?: number;
  failed_count?: number;
} {
  if (!header) return { skipped: [], failed: [] };
  try {
    const parsed = JSON.parse(header);
    return {
      skipped: Array.isArray(parsed?.skipped) ? parsed.skipped : [],
      failed: Array.isArray(parsed?.failed) ? parsed.failed : [],
      success_count: Number(parsed?.success_count ?? 0),
      skipped_count: Number(parsed?.skipped_count ?? 0),
      failed_count: Number(parsed?.failed_count ?? 0),
    };
  } catch {
    return { skipped: [], failed: [] };
  }
}

export const manifestApi = createApi({
  reducerPath: "manifestApi",
  baseQuery: fetchBaseQuery({
    baseUrl: API_ENDPOINT + "/admin/manifests",
    credentials: "include",
  }),
  endpoints: (build) => ({
    createManifest: build.mutation<ManifestCreateResponse, CreateManifestPayload>({
      query: (body) => ({
        url: "/",
        method: "POST",
        body,
      }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(fulfilmentApi.util.invalidateTags(["FulfilmentPayments", "FulfilmentSummary"]));
        } catch {
          /* toast handled by caller */
        }
      },
    }),

    generateManifestPdf: build.mutation<
      Blob,
      { id: string; sku_item_id?: string }
    >({
      query: ({ id, sku_item_id }) => ({
        url: `/${id}/pdf`,
        method: "POST",
        params: sku_item_id ? { sku_item_id } : undefined,
        responseHandler: async (response) => response.blob(),
      }),
    }),

    bulkCreateManifests: build.mutation<
      BulkManifestResponse,
      { cart_order_ids: string[] }
    >({
      query: (body) => ({
        url: "/bulk",
        method: "POST",
        body,
      }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(fulfilmentApi.util.invalidateTags(["FulfilmentPayments", "FulfilmentSummary"]));
        } catch {
          /* toast handled by caller */
        }
      },
    }),

    bulkRevertManifests: build.mutation<
      BulkManifestResponse,
      { cart_order_ids: string[] }
    >({
      query: (body) => ({
        url: "/bulk-revert",
        method: "POST",
        body,
      }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(fulfilmentApi.util.invalidateTags(["FulfilmentPayments", "FulfilmentSummary"]));
        } catch {
          /* toast handled by caller */
        }
      },
    }),

    bulkManifestPdf: build.mutation<
      BulkManifestPdfResult,
      { cart_order_ids: string[] }
    >({
      async queryFn(arg) {
        try {
          const response = await fetch(`${API_ENDPOINT}/admin/manifests/bulk-pdf`, {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(arg),
          });
          if (!response.ok) {
            const data = await response.json().catch(() => ({
              message: "Failed to download labels",
            }));
            return { error: { status: response.status, data } };
          }
          const parsed = parseBulkResultsHeader(
            response.headers.get("X-Bulk-Results"),
          );
          const blob = await response.blob();
          return {
            data: {
              blob,
              success_count:
                parsed.success_count ??
                Number(response.headers.get("X-Bulk-Success-Count") || 0),
              skipped_count:
                parsed.skipped_count ??
                Number(response.headers.get("X-Bulk-Skipped-Count") || 0),
              failed_count:
                parsed.failed_count ??
                Number(response.headers.get("X-Bulk-Failed-Count") || 0),
              skipped: parsed.skipped,
              failed: parsed.failed,
            },
          };
        } catch (err: any) {
          return {
            error: {
              status: 500,
              data: { message: err?.message || "Failed to download labels" },
            },
          };
        }
      },
    }),
  }),
});

export const {
  useCreateManifestMutation,
  useGenerateManifestPdfMutation,
  useBulkCreateManifestsMutation,
  useBulkRevertManifestsMutation,
  useBulkManifestPdfMutation,
} = manifestApi;
